import { useEffect, useRef } from 'react';
import { AppState } from 'react-native';
import EventSource from 'react-native-sse';
import { netDebug } from '../utils/netDebug';

import { buildProductStreamUrl } from '../api/products';
import { useAppDispatch } from '../redux/helper';
import {
  mergeProductDocs,
  removeProductById,
} from '../redux/productSlice';
import type { ProductObject } from '../redux/productSlice';

type ProductStreamData =
  | {
      event: 'created' | 'updated';
      product: ProductObject;
      cursor: null;
      at: string;
    }
  | {
      event: 'deleted';
      id: string;
      cursor: null;
      at: string;
    }
  | {
      locationId: string;
      cursor: null;
      serverTime: string;
    };

type ProductStreamEnvelope = {
  status: number;
  data?: ProductStreamData;
  message?: string;
};

type UseProductSyncStreamArgs = {
  enabled: boolean;
  baseUrl: string;
  accessToken: string;
  locationId: string;
  reconcileProducts: () => void | Promise<void>;
  onTokenExpired?: () => void;
};

const parseStreamEnvelope = (event: any): ProductStreamEnvelope | null => {
  try {
    return typeof event?.data === 'string'
      ? JSON.parse(event.data)
      : event?.data || null;
  } catch {
    return null;
  }
};

export const useProductSyncStream = ({
  enabled,
  baseUrl,
  accessToken,
  locationId,
  reconcileProducts,
  onTokenExpired,
}: UseProductSyncStreamArgs) => {
  const dispatch = useAppDispatch();
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryCountRef = useRef(0);
  const reconcileRef = useRef(reconcileProducts);
  const appIsActiveRef = useRef(true);
  const onTokenExpiredRef = useRef(onTokenExpired);

  reconcileRef.current = reconcileProducts;
  onTokenExpiredRef.current = onTokenExpired;

  useEffect(() => {
    if (!enabled || !baseUrl || !accessToken || !locationId) return;

    let closedByCleanup = false;
    let authErrorSeen = false;

    const clearReconnectTimer = () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };

    const closeStream = () => {
      try {
        if (eventSourceRef.current) netDebug.sseClose('product');
        eventSourceRef.current?.close();
      } catch {}
      eventSourceRef.current = null;
    };

    const runReconcile = () => {
      void reconcileRef.current();
    };

    const scheduleReconnect = (connect: () => void) => {
      if (closedByCleanup || !appIsActiveRef.current) return;

      clearReconnectTimer();
      const delay = Math.min(30000, 1000 * 2 ** retryCountRef.current);
      retryCountRef.current += 1;

      reconnectTimerRef.current = setTimeout(() => {
        connect();
      }, delay);
    };

    const connect = () => {
      if (closedByCleanup || !appIsActiveRef.current) return;

      clearReconnectTimer();
      closeStream();

      const url = buildProductStreamUrl(baseUrl, accessToken, locationId);
      const headers = {
        Authorization: `Bearer ${accessToken}`,
        'X-Location-ID': locationId,
      };

      const es = new EventSource(url, { headers });
      netDebug.sseOpen('product');
      const esAny = es as any;

      const handleConnected = (event: any) => {
        // Effect cleanup fires synchronously with the location-id dep change,
        // but react-native-sse can still deliver queued events to this
        // closure. Drop anything past cleanup — reconcile/writes there would
        // target the previous location's caches.
        if (closedByCleanup) return;
        const envelope = parseStreamEnvelope(event);
        if (envelope?.status !== 200) return;

        retryCountRef.current = 0;
        authErrorSeen = false;
        runReconcile();
      };

      const handleProductEvent = (event: any) => {
        if (closedByCleanup) return;
        const envelope = parseStreamEnvelope(event);
        if (envelope?.status !== 200 || !envelope.data) return;

        const data = envelope.data;

        if ('event' in data && data.event === 'deleted') {
          dispatch(removeProductById(data.id));
          return;
        }

        if (
          'event' in data &&
          (data.event === 'created' || data.event === 'updated') &&
          data.product
        ) {
          dispatch(mergeProductDocs([data.product]));
        }
      };

      const handleError = (event?: any) => {
        const envelope = parseStreamEnvelope(event);
        const isAuthError = envelope?.status === 401 || envelope?.status === 403;

        if (isAuthError && !authErrorSeen) {
          authErrorSeen = true;
          console.warn('Product stream: auth error, waiting for token refresh');
          closeStream();
          onTokenExpiredRef.current?.();
          reconnectTimerRef.current = setTimeout(() => {
            if (closedByCleanup || !appIsActiveRef.current) return;
            authErrorSeen = false;
            connect();
          }, 30000);
          return;
        }

        if (envelope?.status && envelope.status !== 200) {
          console.warn('Product stream error:', envelope.message);
        }

        closeStream();
        if (!authErrorSeen) {
          scheduleReconnect(connect);
        }
      };

      esAny.addEventListener('connected', handleConnected);
      esAny.addEventListener('product:created', handleProductEvent);
      esAny.addEventListener('product:updated', handleProductEvent);
      esAny.addEventListener('product:deleted', handleProductEvent);
      esAny.addEventListener('ping', () => {});
      es.addEventListener('error', handleError);

      eventSourceRef.current = es;
    };

    appIsActiveRef.current = true;
    connect();

    const appStateSubscription = AppState.addEventListener(
      'change',
      nextState => {
        if (nextState === 'active') {
          appIsActiveRef.current = true;
          runReconcile();
          connect();
          return;
        }

        if (nextState === 'background' || nextState === 'inactive') {
          appIsActiveRef.current = false;
          clearReconnectTimer();
          closeStream();
        }
      },
    );

    return () => {
      closedByCleanup = true;
      clearReconnectTimer();
      appStateSubscription.remove();
      closeStream();
    };
  }, [enabled, baseUrl, accessToken, locationId, dispatch]);
};
